// CANONICAL COPY (MNSA-01, the GH #7115 pattern). src/utils/controlOwnerStatus.js re-exports this file so the browser and the
// Function App share one definition. Edit here, never there.
'use strict';

/**
 * src/utils/controlOwnerStatus.js
 *
 * One reading of "is this control's owner still a real, current person?", for
 * every surface that renders a control.
 *
 * WHY THIS IS DERIVED AND NOT AN EVENT (GH #6959)
 * ----------------------------------------------
 * The founder's requirement is that a departure is flagged to admins and shown
 * on the control everywhere the control appears. The obvious build is to fire
 * something when an employee is marked departed — and it would be wrong, because
 * there is more than one route to that status: the JML sweeper advances a
 * `leave` process server-side (`shared/employees/jmlSweeper.js`), and an
 * administrator can set it by hand from the employee record. A hook on one route
 * covers half the cases and looks finished.
 *
 * Deriving it instead means the flag cannot be missed no matter how somebody
 * became departed, cannot drift out of date, and needs no backfill for the
 * people who already left before this shipped — which, on a live product, is
 * most of them. A rule governs a judgement; it is a poor way to govern a
 * mechanism, and here the mechanism can simply be closed.
 *
 * The resolution itself comes from `controlOwnerResolution.js`, which is also
 * what `ownershipGaps.js` and the control-owner portal read, so the tag on the
 * control, the dashboard widget and the portal can never disagree (COP-B-004).
 */

const { LEGACY_TO_SCF } = require('./state/migrateState.cjs');
const { readOwnerId, resolveControlOwner } = require('./controlOwnerResolution.cjs');

/**
 * What each unresolved status means to somebody looking at a control, in words
 * an administrator can act on. `title` is the tooltip: it says what to DO,
 * because a tag that only names a problem leaves the reader hunting.
 */
const OWNER_STATUS_TAG = {
  departed: {
    label: 'Owner has left',
    short: 'Owner left',
    tone: 'danger',
    icon: '⊘',
    title:
      'The person who owns this control no longer works here. Assign a new owner — until you do, nobody is accountable for it.',
  },
  ambiguous: {
    label: 'Owner is ambiguous',
    short: 'Owner unclear',
    tone: 'danger',
    icon: '⁇',
    title:
      'More than one current employee record carries this owner’s email address, so the product cannot tell which person is meant. Resolve the duplicate records, or assign the owner again.',
  },
  'unknown-owner': {
    label: 'Owner account is gone',
    short: 'Owner gone',
    tone: 'danger',
    icon: '⊘',
    title: 'The user account this control was assigned to no longer exists. Assign a new owner.',
  },
  'no-portal-identity': {
    label: 'Owner has no personnel record',
    short: 'No record',
    tone: 'warn',
    icon: '?',
    title:
      'This control’s owner has a login but no employee record, so they cannot be reached through the employee portal and will not be asked to answer its obligations. Add their personnel record, or assign a different owner.',
  },
};

/** The statuses that mean somebody has to do something about the ownership. */
function isOwnerAlert(status) {
  return Object.prototype.hasOwnProperty.call(OWNER_STATUS_TAG, status);
}

/**
 * Resolve one control's owner.
 *
 * @param {object} state    tenant state (controlOwnership, employees, tenantDomains)
 * @param {Array}  users    tenant users
 * @param {string} controlId
 * @param {string} [scfId]  its TCF id, when the caller already has it
 * @returns {object} the discriminated resolution from controlOwnerResolution.js
 */
function controlOwnerStatus(state, users, controlId, scfId) {
  const canonical = scfId || LEGACY_TO_SCF[controlId] || controlId;
  const ownerId = readOwnerId(state?.controlOwnership, controlId, canonical);
  return resolveControlOwner({
    ownerId,
    users,
    employees: state?.employees,
    tenantDomains: state?.tenantDomains,
  });
}

/** The tag to render for a control, or null when its owner is fine (or absent). */
function controlOwnerTag(state, users, controlId, scfId) {
  const resolution = controlOwnerStatus(state, users, controlId, scfId);
  if (!isOwnerAlert(resolution.status)) return null;
  return { ...OWNER_STATUS_TAG[resolution.status], status: resolution.status, resolution };
}

/**
 * Every control whose owner needs attention — the admin-facing list.
 *
 * Used by the Action Centre so a departure reaches the people who can reassign
 * it, rather than waiting to be noticed on a dashboard widget somebody has to
 * open. Derived on read, so it is already correct for the people who left
 * before this shipped — which, on a live product, is most of them.
 *
 * Keyed off `state.controlOwnership` rather than the control catalogue, on
 * purpose: the population this is about is exactly "controls that have an owner
 * recorded", and that is what the map holds. It needs no framework list, no
 * enrichment and no catalogue import, so it is cheap enough to run on every
 * render of the badge.
 *
 * @returns {Array<{controlId: string, status: string, tag: object, resolution: object}>}
 */
function listControlOwnerAlerts(state, users) {
  const ownership = state?.controlOwnership;
  if (!ownership || typeof ownership !== 'object') return [];

  // A control excluded from the Statement of Applicability has no owner to
  // chase — flagging it would be noise on a decision somebody already took.
  const excluded = new Set();
  for (const fw of Object.values(state.soaData || {})) {
    for (const [cid, entry] of Object.entries(fw.controls || {})) {
      if (entry && entry.applicable === false) excluded.add(cid);
    }
  }

  const employees = state.employees;
  const tenantDomains = state.tenantDomains;
  const out = [];

  for (const [controlId, record] of Object.entries(ownership)) {
    if (excluded.has(controlId)) continue;
    const ownerId = record?.ownerId || null;
    // An UNOWNED control is a different finding, already reported by
    // ownershipGaps as 'unowned' and shown on the dashboard. This list is about
    // owners that LOOK assigned and are not — the case nothing told anybody
    // about, which is what GH #6959 is.
    if (!ownerId) continue;
    const resolution = resolveControlOwner({ ownerId, users, employees, tenantDomains });
    if (!isOwnerAlert(resolution.status)) continue;
    out.push({
      controlId,
      status: resolution.status,
      tag: OWNER_STATUS_TAG[resolution.status],
      resolution,
    });
  }
  return out;
}

module.exports = {
  OWNER_STATUS_TAG,
  isOwnerAlert,
  controlOwnerStatus,
  controlOwnerTag,
  listControlOwnerAlerts,
};
